import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { DecimalKeypad, TextKeypad } from '@/components/ui/keypads';
import { ExpenseModalProps, ExpenseFormData, ExpenseFormErrors } from './ExpenseModal.types';
import { cashMovementCategoriesService, CashMovementCategory } from '../../services/cashMovementCategoriesService';

export const ExpenseModal: React.FC<ExpenseModalProps> = ({ isOpen, onClose, onSubmit, type, locationId }) => {
  const [formData, setFormData] = useState<ExpenseFormData>({
    description: '',
    amount: '',
    category: '',
  });

  const [errors, setErrors] = useState<ExpenseFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [textKeypadOpen, setTextKeypadOpen] = useState(false);
  const [categories, setCategories] = useState<CashMovementCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Load categories when modal opens or type changes
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen, type]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        description: '',
        amount: '',
        category: '',
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      console.log('🔍 Loading categories for type:', type);
      console.log('🌐 Making request to API...');
      const response = await cashMovementCategoriesService.getCategoriesByType(type);
      console.log('📦 API Response received:', response);
      if (response && response.success) {
        console.log('✅ Categories loaded successfully:', response.data);
        setCategories(response.data);
      } else {
        console.log('❌ API response not successful:', response);
        setCategories([]);
      }
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      console.error('❌ Error details:', error.message, error.stack);
      // Fallback to hardcoded categories if API fails
      setCategories([]);
    } finally {
      console.log('🏁 Finished loading categories, setting loading to false');
      setLoadingCategories(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ExpenseFormErrors = {};

    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = 'El monto debe ser mayor a 0';
    }

    if (!formData.category) {
      newErrors.category = 'La categoría es requerida';
    }

    // Descripción es opcional

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({
        description: formData.description.trim(),
        amount: parseFloat(formData.amount),
        category: formData.category,
        type: type,
        locationId: locationId,
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting expense:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ExpenseFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const openAmountKeypad = () => {
    setKeypadOpen(true);
  };

  const openDescriptionKeypad = () => {
    setTextKeypadOpen(true);
  };

  const handleKeypadSubmit = (value: string) => {
    handleInputChange('amount', value);
  };

  const handleTextKeypadSubmit = (value: string) => {
    handleInputChange('description', value);
  };

  // Transform API categories to match the component's expected format
  const categoryOptions = categories.map(category => ({
    value: category.code,
    label: category.name,
    icon: category.icon,
    requiresApproval: category.requiresApproval
  }));

  // Fallback categories if API is not available
  const fallbackCategories = type === 'income'
    ? [
        { value: 'income', label: 'Ingreso adicional', icon: 'plus-circle', requiresApproval: false },
        { value: 'refund', label: 'Reembolso', icon: 'undo', requiresApproval: false },
        { value: 'bonus', label: 'Bonificación', icon: 'gift', requiresApproval: false },
        { value: 'cashback', label: 'Devolución', icon: 'rotate-ccw', requiresApproval: false },
      ]
    : [
        { value: 'ingredients', label: 'Ingredientes', icon: 'package', requiresApproval: false },
        { value: 'services', label: 'Servicios', icon: 'settings', requiresApproval: false },
        { value: 'utilities', label: 'Servicios Básicos', icon: 'zap', requiresApproval: false },
        { value: 'transport', label: 'Transporte', icon: 'car', requiresApproval: false },
        { value: 'others', label: 'Otros', icon: 'more-horizontal', requiresApproval: false },
      ];

  const displayCategories = categoryOptions.length > 0 ? categoryOptions : fallbackCategories;

  // Debug log
  console.log('🏷️ Categories state:', {
    categories: categories.length,
    categoryOptions: categoryOptions.length,
    fallbackCategories: fallbackCategories.length,
    displayCategories: displayCategories.length,
    type,
    loadingCategories,
    actualCategories: categories,
    actualCategoryOptions: categoryOptions,
    isOpen
  });

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{type === 'income' ? 'Agregar Ingreso' : 'Agregar Gasto'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Categoría - Grid de 2 columnas para múltiples opciones */}
            <div className="space-y-3">
              <Label htmlFor="category" className="text-base font-semibold">Categoría</Label>
              {loadingCategories ? (
                <div className="col-span-2 text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-macartur-primary mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Cargando categorías...</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {displayCategories.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant={formData.category === option.value ? "default" : "outline"}
                    className={`h-12 sm:h-14 text-center justify-center text-xs sm:text-sm transition-all duration-200 px-2 sm:px-4 ${
                      formData.category === option.value
                        ? 'bg-macartur-primary text-white shadow-md border-macartur-primary'
                        : 'bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
                    } ${errors.category ? 'border-red-500' : ''}`}
                    onClick={() => handleInputChange('category', option.value)}
                  >
                    <div className="flex flex-col items-center gap-1 sm:gap-2">
                      <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.category === option.value
                          ? 'border-white bg-white'
                          : 'border-gray-400'
                      }`}>
                        {formData.category === option.value && (
                          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-macartur-primary"></div>
                        )}
                      </div>
                      <span className="text-center leading-tight">{option.label}</span>
                    </div>
                  </Button>
                  ))}
                </div>
              )}
              {errors.category && (
                <p className="text-sm text-red-500">{errors.category}</p>
              )}
            </div>

            {/* Monto */}
            <div className="space-y-2">
              <Label htmlFor="amount" className="text-base font-semibold">Monto</Label>
              <Input
                id="amount"
                type="text"
                value={formData.amount ? `S/ ${parseFloat(formData.amount).toFixed(2)}` : 'S/ 0.00'}
                onClick={openAmountKeypad}
                readOnly
                placeholder="S/ 0.00"
                className={`cursor-pointer bg-white hover:bg-gray-50 transition-colors h-12 text-lg ${errors.amount ? 'border-red-500' : ''}`}
              />
              {errors.amount && (
                <p className="text-sm text-red-500">{errors.amount}</p>
              )}
            </div>

            {/* Descripción - Opcional, al final */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-base font-medium text-gray-600">
                Descripción <span className="text-sm text-gray-400">(opcional)</span>
              </Label>
              <Input
                id="description"
                value={formData.description}
                onClick={openDescriptionKeypad}
                readOnly
                placeholder={type === 'income' ? 'Ej: Reembolso, devolución, etc.' : 'Ej: Compra de ingredientes'}
                className={`cursor-pointer bg-white hover:bg-gray-50 transition-colors h-12 ${errors.description ? 'border-red-500' : ''}`}
              />
              {errors.description && (
                <p className="text-sm text-red-500">{errors.description}</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Agregando...' : (type === 'income' ? 'Agregar Ingreso' : 'Agregar Gasto')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Decimal Keypad Modal - Rendered separately to avoid nesting */}
      <DecimalKeypad
        isOpen={keypadOpen}
        onClose={() => setKeypadOpen(false)}
        onSubmit={handleKeypadSubmit}
        title="Ingresa el monto"
        initialValue={formData.amount || '0.00'}
      />

      {/* Text Keypad Modal - For description input */}
      <TextKeypad
        isOpen={textKeypadOpen}
        onClose={() => setTextKeypadOpen(false)}
        onSubmit={handleTextKeypadSubmit}
        title="Ingresa la descripción"
        initialValue={formData.description}
        placeholder={type === 'income' ? 'REEMBOLSO, DEVOLUCION, ETC.' : 'COMPRA DE INGREDIENTES'}
      />
    </>
  );
};